import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FilterChip,
  FormField,
  FormTextarea,
  Label,
  Select,
} from "@/components/ui";
import { CONTACT_KIND_LABELS, CONTACT_KINDS, type ContactKind } from "@/api/types/contact";
import { useCreateCampaign, useSegmentPreview } from "@/api/hooks/useCampaigns";
import type { Campaign, Segment } from "@/api/types/campaign";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { TagsInput } from "@/features/contacts/TagsInput";

interface CampaignBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (campaign: Campaign) => void;
}

type WarmFilter = "all" | "warm" | "cold";

const EMPTY_STATE = {
  name: "",
  subject: "",
  bodyTemplate: "",
  personalize: false,
  kinds: [] as ContactKind[],
  warm: "all" as WarmFilter,
  tags: [] as string[],
};

/**
 * Campaign builder — name/subject/body + a segment builder with a live
 * eligible-recipient count. Saves as a draft; sending happens from the
 * campaign detail page (per contract: POST is a separate /send call).
 */
export function CampaignBuilderDialog({ open, onClose, onSaved }: CampaignBuilderDialogProps) {
  const [name, setName] = useState(EMPTY_STATE.name);
  const [subject, setSubject] = useState(EMPTY_STATE.subject);
  const [bodyTemplate, setBodyTemplate] = useState(EMPTY_STATE.bodyTemplate);
  const [personalize, setPersonalize] = useState(EMPTY_STATE.personalize);
  const [kinds, setKinds] = useState<ContactKind[]>(EMPTY_STATE.kinds);
  const [warm, setWarm] = useState<WarmFilter>(EMPTY_STATE.warm);
  const [tags, setTags] = useState<string[]>(EMPTY_STATE.tags);
  const [nameError, setNameError] = useState<string | null>(null);

  const createCampaign = useCreateCampaign();

  useEffect(() => {
    if (open) {
      setName(EMPTY_STATE.name);
      setSubject(EMPTY_STATE.subject);
      setBodyTemplate(EMPTY_STATE.bodyTemplate);
      setPersonalize(EMPTY_STATE.personalize);
      setKinds(EMPTY_STATE.kinds);
      setWarm(EMPTY_STATE.warm);
      setTags(EMPTY_STATE.tags);
      setNameError(null);
    }
  }, [open]);

  const segment: Segment = {
    kinds: kinds.length ? kinds : undefined,
    warm: warm === "all" ? undefined : warm,
    tags: tags.length ? tags : undefined,
  };

  const { data: preview, isLoading: previewLoading, error: previewError } =
    useSegmentPreview(segment);

  function toggleKind(kind: ContactKind) {
    setKinds((current) =>
      current.includes(kind) ? current.filter((k) => k !== kind) : [...current, kind],
    );
  }

  async function handleSubmit() {
    if (!name.trim()) {
      setNameError("Name is required");
      return;
    }
    if (!subject.trim()) {
      toastError("Couldn't save campaign", "Subject is required");
      return;
    }
    try {
      const campaign = await createCampaign.mutateAsync({
        name: name.trim(),
        subject: subject.trim(),
        body_template: bodyTemplate,
        segment,
        personalize,
      });
      toastSuccess("Campaign saved as draft");
      onSaved(campaign);
    } catch (err) {
      toastError("Couldn't save campaign", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="New campaign">
      <DialogContent size="lg">
        <DialogHeader onClose={onClose}>New campaign</DialogHeader>
        <DialogBody className="space-y-5">
          <FormField
            label="Campaign name"
            required
            value={name}
            error={nameError ?? undefined}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
          />

          <FormField
            label="Subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <div className="space-y-2">
            <FormTextarea
              label="Email body"
              rows={6}
              value={bodyTemplate}
              onChange={(e) => setBodyTemplate(e.target.value)}
              placeholder="Hi {first_name}, ..."
            />
            <p className="text-xs text-text-muted">
              Use <code className="rounded bg-bg-muted px-1">{"{name}"}</code> or{" "}
              <code className="rounded bg-bg-muted px-1">{"{first_name}"}</code> to personalize
              with the recipient's own name. When "Personalize with AI" is on below, the email is
              additionally tailored per recipient beyond simple token substitution.
            </p>
          </div>

          <Checkbox
            label="Personalize with AI"
            checked={personalize}
            onChange={(e) => setPersonalize(e.target.checked)}
          />

          <div className="space-y-3 border-t border-border pt-4">
            <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
              Segment
            </p>

            <div className="space-y-2">
              <Label>Contact kinds</Label>
              <div className="flex flex-wrap gap-2">
                {CONTACT_KINDS.map((kind) => (
                  <FilterChip
                    key={kind}
                    data-testid={`segment-kind-${kind}`}
                    active={kinds.includes(kind)}
                    onClick={() => toggleKind(kind)}
                    label={CONTACT_KIND_LABELS[kind]}
                  />
                ))}
              </div>
              <p className="text-xs text-text-muted">Leave empty to include every kind.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="campaign-warm">Warm / cold</Label>
                <Select
                  id="campaign-warm"
                  value={warm}
                  onChange={(e) => setWarm(e.target.value as WarmFilter)}
                >
                  <option value="all">All</option>
                  <option value="warm">Warm only</option>
                  <option value="cold">Cold only</option>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <TagsInput value={tags} onChange={setTags} placeholder="Filter by tag..." />
              </div>
            </div>

            <div
              data-testid="segment-preview-count"
              className="rounded-md border border-border bg-bg-hover/50 px-3 py-2 text-sm"
            >
              {previewLoading ? (
                <span className="text-text-muted">Checking eligible recipients...</span>
              ) : previewError ? (
                <span className="text-danger">Couldn't load segment preview</span>
              ) : (
                <span className="font-medium text-text-primary">
                  {preview?.count ?? 0} eligible recipient{preview?.count === 1 ? "" : "s"}
                </span>
              )}
            </div>

            <p className="rounded-md border border-copper/30 bg-copper/5 px-3 py-2 text-xs text-text-secondary">
              Only contacts who opted in to email are included — consent is enforced
              automatically and can't be overridden here.
            </p>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={createCampaign.isPending}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={createCampaign.isPending}>
            {createCampaign.isPending ? "Saving..." : "Save as draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
